import { useState } from "react";
import { Activity, Sparkles, Check, MessageCircle, CreditCard, Loader2 } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { SITE } from "@/lib/site";
import { startCheckout } from "@/lib/payments";

const Therapy = () => {
    const { t, lang } = useLanguage();
    const T = t.therapy;
    const [busy, setBusy] = useState(false);

    const buyTherapy = async () => {
        setBusy(true);
        try {
            await startCheckout({ productId: "therapy_dorn_breuss", language: lang });
        } finally {
            setBusy(false);
        }
    };

    return (
        <section
            id="therapy"
            data-testid="therapy-section"
            className="py-24 md:py-32 bg-ella-cream relative overflow-hidden"
        >
            {/* Soft brand accents */}
            <div className="absolute -top-32 right-0 w-[480px] h-[480px] rounded-full bg-[radial-gradient(circle_at_center,rgba(193,154,107,0.10),transparent_60%)] pointer-events-none" />
            <div className="absolute -bottom-40 -left-32 w-[420px] h-[420px] rounded-full bg-[radial-gradient(circle_at_center,rgba(31,29,28,0.06),transparent_60%)] pointer-events-none" />

            <div className="ella-container relative">
                {/* Header */}
                <div className="text-center max-w-3xl mx-auto">
                    <div className="inline-flex items-center gap-3">
                        <span className="gold-hairline" />
                        <span className="ella-overline">{T.overline}</span>
                        <span className="gold-hairline" />
                    </div>
                    <h2
                        className="ella-headline text-4xl md:text-5xl lg:text-6xl mt-5 text-ella-ink"
                        data-testid="therapy-title"
                    >
                        {T.title}
                    </h2>
                    <p className="mt-6 text-lg text-ella-ink/70 font-light">
                        {T.subtitle}
                    </p>
                </div>

                <p
                    className="mt-10 max-w-3xl mx-auto text-center text-ella-ink/75 leading-relaxed font-light text-[17px]"
                    data-testid="therapy-intro"
                >
                    {T.intro}
                </p>

                {/* Methods grid */}
                <div className="mt-16 grid md:grid-cols-2 gap-6 lg:gap-8">
                    {T.methods.map((m, i) => (
                        <article
                            key={i}
                            data-testid={`therapy-method-${i}`}
                            className="ella-card !p-10 md:!p-12 group"
                        >
                            <div className="flex items-start justify-between">
                                <span className="w-14 h-14 rounded-2xl bg-ella-ink flex items-center justify-center text-ella-gold group-hover:bg-ella-gold group-hover:text-white transition-all duration-500">
                                    {i === 0 ? (
                                        <Activity className="w-5 h-5" strokeWidth={1.4} />
                                    ) : (
                                        <Sparkles className="w-5 h-5" strokeWidth={1.4} />
                                    )}
                                </span>
                                <span className="ella-overline">{T.method_label} 0{i + 1}</span>
                            </div>
                            <h3 className="font-display text-2xl md:text-3xl mt-7 text-ella-ink leading-tight">
                                {m.name}
                            </h3>
                            <p className="mt-4 text-ella-ink/72 leading-relaxed font-light text-[15px]">
                                {m.desc}
                            </p>
                        </article>
                    ))}
                </div>

                {/* Benefits + Investment block */}
                <div className="mt-16 grid lg:grid-cols-12 gap-8 items-stretch">
                    {/* Benefits */}
                    <div className="lg:col-span-7 ella-card !p-10 md:!p-12">
                        <span className="ella-overline">{T.benefits_label}</span>
                        <h3 className="font-display text-3xl md:text-4xl mt-3 text-ella-ink">
                            {t.benefits.title}
                        </h3>
                        <ul className="mt-7 space-y-4">
                            {T.benefits.map((b, i) => (
                                <li
                                    key={i}
                                    data-testid={`therapy-benefit-${i}`}
                                    className="flex items-start gap-3.5 text-ella-ink/85 text-[15px] leading-relaxed"
                                >
                                    <span className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-ella-sand flex items-center justify-center">
                                        <Check className="w-3 h-3 text-ella-gold-dark" strokeWidth={2} />
                                    </span>
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Investment / Pricing */}
                    <div
                        className="lg:col-span-5 ella-card-dark !p-10 md:!p-12 flex flex-col justify-between"
                        data-testid="therapy-pricing"
                    >
                        <div>
                            <span className="ella-overline-light">{T.details_label}</span>
                            <div className="mt-5 flex items-baseline gap-2">
                                <span className="font-display text-6xl md:text-7xl text-ella-champagne leading-none">
                                    185
                                </span>
                                <span className="text-sm uppercase tracking-[0.22em] text-ella-gold">
                                    CHF
                                </span>
                            </div>
                            <p className="mt-2 text-ella-cream/70 text-sm font-light">
                                {T.per_session}
                            </p>

                            <div className="mt-9 pt-7 border-t border-ella-line-dark">
                                <span className="ella-overline-light">{T.duration_label}</span>
                                <p className="mt-2 font-display text-2xl text-ella-cream">
                                    {T.duration}
                                </p>
                            </div>
                        </div>

                        <div className="mt-10 space-y-3">
                            <button
                                onClick={buyTherapy}
                                disabled={busy}
                                data-testid="therapy-cta-buy"
                                className="btn-gold w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {busy ? (
                                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                                ) : (
                                    <CreditCard className="w-4 h-4" strokeWidth={1.5} />
                                )}
                                {T.cta}
                            </button>
                            <a
                                href={`${SITE.contact.whatsappUrl}?text=${encodeURIComponent("Hallo Mercedes, ich interessiere mich für die Dorn-Breuss-Therapie.")}`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="therapy-cta-whatsapp"
                                className="btn-outline-light w-full justify-center"
                            >
                                <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                                {T.cta_alt}
                            </a>
                        </div>
                    </div>
                </div>

                {/* Disclaimer */}
                <p
                    className="mt-12 text-center text-xs text-ella-muted italic max-w-2xl mx-auto leading-relaxed"
                    data-testid="therapy-disclaimer"
                >
                    {T.disclaimer}
                </p>
            </div>
        </section>
    );
};

export default Therapy;
